import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useSEO } from '@/hooks/useSEO';
import { useTherapyModalities, getTherapiesByStage, getEvidenceLevels, getStreamTypes } from '@/hooks/useTherapyModalities';
import { useTherapyModalityStats, TherapyModalityStats } from '@/hooks/useTherapyModalityStats';
import { useBotProtection } from '@/hooks/useBotProtection';
import TherapyFilters from '@/components/therapy/TherapyFilters';
import StageSection from '@/components/therapy/StageSection';
import TallySurveyEmbed from '@/components/embeds/TallySurveyEmbed';
import { Accordion } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';

const TherapyWikipedia: React.FC = () => {
  const { t, i18n } = useTranslation('therapyLibrary');
  const isZh = i18n.language === 'zh';
  const { isBot, isReady } = useBotProtection();

  const { data: therapies, isLoading, error, refetch } = useTherapyModalities();
  const { data: stats } = useTherapyModalityStats();

  // Create a map of therapy_modality_id -> stats for efficient lookup
  const statsMap = useMemo(() => {
    if (!stats) return new Map<string, TherapyModalityStats>();
    return new Map(stats.map(s => [s.therapy_modality_id, s]));
  }, [stats]);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStage, setSelectedStage] = useState('all');
  const [selectedEvidenceLevel, setSelectedEvidenceLevel] = useState('all');
  const [selectedStreamType, setSelectedStreamType] = useState('all');
  const [expandedStages, setExpandedStages] = useState<string[]>([]);

  // SEO
  useSEO({
    title: t('pageTitle'),
    description: t('pageSubtitle'),
  });

  // Derived data
  const evidenceLevels = useMemo(() => therapies ? getEvidenceLevels(therapies) : [], [therapies]);
  const streamTypes = useMemo(() => therapies ? getStreamTypes(therapies) : [], [therapies]);

  // Filter therapies
  const filteredTherapies = useMemo(() => {
    if (!therapies) return [];

    return therapies.filter(therapy => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = therapy.name_en.toLowerCase().includes(query) || 
                           therapy.name_cn.toLowerCase().includes(query);
        const matchesMechanism = (therapy.mechanism_en?.toLowerCase().includes(query)) ||
                                 (therapy.mechanism_cn?.toLowerCase().includes(query));
        const matchesCert = therapy.certifications?.toLowerCase().includes(query);
        
        if (!matchesName && !matchesMechanism && !matchesCert) return false;
      }

      // Stage filter
      if (selectedStage !== 'all') {
        const stageKey = `stage_${selectedStage}` as keyof typeof therapy;
        if (!therapy[stageKey]) return false;
      }

      // Evidence level filter
      if (selectedEvidenceLevel !== 'all' && therapy.evidence_level !== selectedEvidenceLevel) {
        return false;
      }

      // Stream type filter
      if (selectedStreamType !== 'all' && therapy.stream_type !== selectedStreamType) {
        return false;
      }

      return true;
    });
  }, [therapies, searchQuery, selectedStage, selectedEvidenceLevel, selectedStreamType]);

  // Group filtered therapies by stage
  const therapiesByStage = useMemo(() => {
    const stages: { [key: number]: typeof filteredTherapies } = {};
    for (let i = 0; i <= 4; i++) {
      stages[i] = getTherapiesByStage(filteredTherapies, i);
    }
    return stages;
  }, [filteredTherapies]);

  // Check if any filters are active
  const hasActiveFilters = searchQuery !== '' || 
                          selectedStage !== 'all' || 
                          selectedEvidenceLevel !== 'all' || 
                          selectedStreamType !== 'all';

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedStage('all');
    setSelectedEvidenceLevel('all');
    setSelectedStreamType('all');
  };

  // Expand/Collapse all
  const expandAll = () => setExpandedStages(['stage-0', 'stage-1', 'stage-2', 'stage-3', 'stage-4']);
  const collapseAll = () => setExpandedStages([]);

  // Bot protection - show loading while detecting
  if (!isReady) {
    return (
      <div className="min-h-screen bg-amber-50/50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
          <span className="text-lg text-earth-700">{t('loading')}</span>
        </div>
      </div>
    );
  }

  // Block detected bots
  if (isBot) {
    return (
      <div className="min-h-screen bg-amber-50/50 flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-2xl font-serif text-earth-900 mb-4">Content Protected</h1>
          <p className="text-earth-600">This content is protected from automated access.</p>
          <p className="text-earth-600 mt-2">Please visit our website using a regular browser.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-amber-50/50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
          <span className="text-lg text-earth-700">{t('loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-amber-50/50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{t('error')}</p>
          <Button onClick={() => refetch()} variant="default">
            {t('retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50/50">
      <Navbar />

      <main className="pt-16 pb-16">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Header Section */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-serif text-earth-900 mb-4">
              {t('pageTitle')}
            </h1>
            <p className="text-lg text-earth-600 max-w-2xl mx-auto">
              {t('pageSubtitle')}
            </p>
          </div>

          {/* Filters */}
          <div className="mb-6">
            <TherapyFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedStage={selectedStage}
              onStageChange={setSelectedStage}
              selectedEvidenceLevel={selectedEvidenceLevel}
              onEvidenceLevelChange={setSelectedEvidenceLevel}
              selectedStreamType={selectedStreamType}
              onStreamTypeChange={setSelectedStreamType}
              evidenceLevels={evidenceLevels}
              streamTypes={streamTypes}
              onClear={clearFilters}
              hasActiveFilters={hasActiveFilters}
            />
          </div>

          {/* Results count and expand/collapse buttons */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <p className="text-sm text-earth-600">
              {t('therapiesCount', { count: filteredTherapies.length })}
            </p>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={expandAll}
                className="text-amber-700 hover:text-amber-900"
              >
                <ChevronDown className="h-4 w-4 mr-1" />
                {t('expandAll')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={collapseAll}
                className="text-amber-700 hover:text-amber-900"
              >
                <ChevronUp className="h-4 w-4 mr-1" />
                {t('collapseAll')}
              </Button>
            </div>
          </div>

          {/* Stage Accordions */}
          <div className="space-y-4">
            <Accordion
              type="multiple"
              value={expandedStages}
              onValueChange={setExpandedStages}
              className="w-full space-y-4"
            >
              {[0, 1, 2, 3, 4].map((stageNum) => (
                <StageSection
                  key={stageNum}
                  stageNumber={stageNum}
                  therapies={therapiesByStage[stageNum]}
                  statsMap={statsMap}
                />
              ))}
            </Accordion>
          </div>

          {/* Healing Stage Survey Section */}
          <section className="mt-12 bg-white rounded-lg border border-amber-100 p-6 shadow-sm text-center">
            <h2 className="text-2xl font-serif text-earth-900 mb-3">
              {isZh ? '探索您的疗愈阶段' : 'Discover Your Healing Stage'}
            </h2>
            <p className="text-earth-700 mb-6">
              {isZh ? '回答几个问题来了解您目前处于哪个阶段。您的回答将保持私密。' : 'Answer a few questions to understand which stage you are in. Your responses stay private.'}
            </p>
            <TallySurveyEmbed />
          </section>

          {/* Call to Action */}
          <div className="mt-12 text-center bg-white rounded-lg border border-amber-100 p-8 shadow-sm">
            <h2 className="text-2xl font-serif text-earth-900 mb-4">
              {t('cta.title')}
            </h2>
            <p className="text-earth-700 mb-6">
              {t('cta.description')}
            </p>
            <Link
              to="/booking-choice"
              className="inline-block bg-amber-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-amber-700 transition-colors"
            >
              {t('cta.button')}
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TherapyWikipedia;
